import React, { useMemo, useState } from 'react';
import styles from './Analytics.module.scss';
import { LevelCard } from './components/LevelCard';
import { ChallengeCard } from './components/ChallengeCard';
import { StatusDistribution } from './components/StatusDistribution';
import { FarmCompletionChart } from './components/FarmCompletionChart';
import { ChallengeAnalyticsService } from '../../services/ChallengeAnalyticsService';
import { useChallengesTable } from '../../api/hooks/useChallenges';
import { useOrganization } from '../../contexts/OrganizationContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import Select, { SelectOption } from '../../components/Select';
import { ChallengeTableResponse } from '../../api/types/dtos/challenge/ChallengeDto';

const Analytics: React.FC = () => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id || '';
  
  // Region filter state
  const [selectedRegion, setSelectedRegion] = useState<SelectOption | undefined>(undefined);
  
  // Region-specific statistics
  const regionStatistics: Record<string, number> = {
    'GLOBAL': 103,
    'Antioquia': 59, // Replace with actual value
    'Nariño': 44,    // Replace with actual value
  };

  // Get the statistic value based on selected region
  const getLevelUpStatistic = (): number => {
    if (!selectedRegion || selectedRegion.value === 'ALL') {
      return regionStatistics['GLOBAL'];
    }
    // Return region-specific value if exists, otherwise return global
    return regionStatistics[selectedRegion.value] || regionStatistics['GLOBAL'];
  };
  
  // Fetch challenges table data
  const {
    data: challengesTableData,
    isLoading: isLoadingChallenges,
    error: challengesError,
  } = useChallengesTable(organizationId);

  // Extract unique regions from challenges data
  const regionOptions = useMemo(() => {
    if (!challengesTableData?.data?.challenges) {
      return [];
    }

    const uniqueRegions = new Set<string>();
    challengesTableData.data.challenges.forEach(challenge => {
      if (challenge.inspectedOrgRegionName) {
        uniqueRegions.add(challenge.inspectedOrgRegionName);
      }
    });

    const options: SelectOption[] = [
      { value: 'ALL', label: 'Todas las Regiones' },
      ...Array.from(uniqueRegions)
        .sort()
        .map(region => ({ value: region, label: region }))
    ];

    return options;
  }, [challengesTableData]);

  // Filter challenges data based on selected region
  const filteredChallengesData = useMemo(() => {
    if (!challengesTableData?.data) {
      return null;
    }

    // If no region selected or "ALL" selected, return all data
    if (!selectedRegion || selectedRegion.value === 'ALL') {
      return challengesTableData;
    }

    // Filter challenges by selected region
    const filteredChallenges = challengesTableData.data.challenges.filter(
      challenge => challenge.inspectedOrgRegionName === selectedRegion.value
    );

    const filteredData: ChallengeTableResponse = {
      ...challengesTableData,
      data: {
        ...challengesTableData.data,
        challenges: filteredChallenges
      }
    };

    return filteredData;
  }, [challengesTableData, selectedRegion]);

  // Process analytics data with filtered data
  const analyticsData = useMemo(() => {
    if (!filteredChallengesData?.data) {
      return null;
    }
    return ChallengeAnalyticsService.processAnalytics(filteredChallengesData);
  }, [filteredChallengesData]);

  if (isLoadingChallenges) {
    return (
      <div className={styles.analyticsContainer}>
        <h1>Visualiza tus Datos</h1>
        <LoadingSpinner />
      </div>
    );
  }

  if (challengesError) {
    return (
      <div className={styles.analyticsContainer}>
        <h1>Visualiza tus Datos</h1>
        <div className={styles.errorMessage}>
          Error cargando los datos: {challengesError.message}
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className={styles.analyticsContainer}>
        <h1>Visualiza tus Datos</h1>
        <div className={styles.noDataMessage}>
          No hay datos disponibles para mostrar analytics.
        </div>
      </div>
    );
  }

  const { levelAnalytics, challengeAnalytics, statusDistribution, farmCompletionDistribution } = analyticsData;

  return (
    <div className={styles.analyticsContainer}>
      <h1>Visualiza tus Datos</h1>
      
      {/* Region Filter */}
      {regionOptions.length > 0 && (
        <div className={styles.filterContainer}>
          <label className={styles.filterLabel}>Filtrar por Región:</label>
          <Select
            options={regionOptions}
            value={selectedRegion}
            onChange={setSelectedRegion}
            placeholder="Todas las Regiones"
            className={styles.regionFilter}
          />
        </div>
      )}
      
      {/* Top Statistic */}
      {/* <div className={styles.statisticCard}>
        <div className={styles.statisticNumber}>{getLevelUpStatistic()}</div>
        <div className={styles.statisticLabel}>Productores que han subido de nivel</div>
      </div> */}
      
      {/* Level Analytics Cards */}
      {levelAnalytics.length > 0 && (
        <>
          <h2>Avance General</h2>
          <div className={styles.levelsGrid}>
            {levelAnalytics.map((levelData) => (
              <LevelCard
                key={levelData.levelId}
                levelAnalytics={levelData}
              />
            ))}
          </div>
        </>
      )}

      {/* Challenge Analytics Cards */}
      {/* {challengeAnalytics.length > 0 && (
        <>
          <h2>Análisis por Reto</h2>
          <div className={styles.challengesGrid}>
            {challengeAnalytics.map((challengeData) => (
              <ChallengeCard
                key={challengeData.challengeId}
                challengeAnalytics={challengeData}
              />
            ))}
          </div>
        </>
      )} */}

      {/* <div className={styles.analyticsGrid}>
        <div className={styles.statusSection}>
          <h2>Distribución de Estados</h2>
          <StatusDistribution statusDistribution={statusDistribution} />
        </div>

        <div className={styles.farmCompletionSection}>
          <h2>Distribución de fincas por tareas completadas</h2>
          <FarmCompletionChart farmCompletionDistribution={farmCompletionDistribution} />
        </div>
      </div> */}
    </div>
  );
};

export default Analytics; 